/**
 * Canonical email normalization for case-insensitive, whitespace-tolerant
 * identity matching (contact_id ⟷ lower(email) joins across the SFDC mirror
 * and the launcher tables). One helper so every call site normalizes the same
 * way — several inline `LOWER()` / `.toLowerCase()` duplications exist today
 * (enroll-service, playhouse/api, member-role-sync); wider adoption is cleanup.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * The single "is this email actually present" predicate (requires-email
 * fixes, 2026-08-12). A raw `!= null` / `!x` check on a stored/incoming
 * email string is not enough — a whitespace-only string (`'   '`) is
 * non-null and truthy, so it slips past those checks even though it carries
 * no usable address. This normalizes via `normalizeEmail` and then collapses
 * the empty-string result to `null`, giving every call site (queue add/CAS/
 * PATCH gates, send-time backstops, UI email-presence checks) one shared
 * definition of "present" instead of each reimplementing its own trim check.
 */
export function canonicalizeEmail(value: string | null | undefined): string | null {
  if (value == null) return null;
  const normalized = normalizeEmail(value);
  return normalized === '' ? null : normalized;
}
